import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react'
import useDeepCompareEffect from 'use-deep-compare-effect'
import useLoadMapScript from 'hooks/useMapScript'
import styles from './Map.module.scss'

interface MapProps extends google.maps.MapOptions {
  onClick?: (e: google.maps.MapMouseEvent) => void
  onIdle?: (map: google.maps.Map) => void
  onLoadedMap?: (map: google.maps.Map) => void
  children?: ReactNode
}

const Map = ({
  onClick,
  onIdle,
  onLoadedMap,
  children,
  ...options
}: MapProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [map, setMap] = useState<google.maps.Map>()
  const { isMapScriptLoaded } = useLoadMapScript()

  useEffect(() => {
    if (ref.current && map === undefined && isMapScriptLoaded) {
      const googleMap = new window.google.maps.Map(ref.current, {
        center: options.center,
        zoom: options.zoom,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        gestureHandling: 'greedy',
        clickableIcons: false,
        minZoom: 13,
        maxZoom: 20,
      })
      setMap(googleMap)
      if (onLoadedMap) onLoadedMap(googleMap)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, map, isMapScriptLoaded])

  useDeepCompareEffect(() => {
    if (map) {
      map.setOptions(options)
    }
  }, [map, options])

  useEffect(() => {
    if (map) {
      const events = ['click', 'idle']
      events.forEach((eventName) =>
        google.maps.event.clearListeners(map, eventName),
      )

      if (onClick) {
        map.addListener('click', onClick)
      }

      if (onIdle) {
        map.addListener('idle', () => onIdle(map))
      }
    }
  }, [map, onClick, onIdle])

  return (
    <>
      <div ref={ref} className={styles.ecSearchMap} data-testid="ecMap" />
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          return cloneElement(child, { map })
        }
        return null
      })}
    </>
  )
}

export default Map
